/**
 * TrackerLayout - Shared layout for Tracker views
 */

import React, { useEffect, useRef } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { getJson } from "@/lib/http";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { RefreshCw, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatDistanceToNow, format } from "date-fns";
import { PageHeader } from "@/components/ui/PageHeader";
import { YearFilter } from "@/components/filters/YearFilter";
import { useYear, useSetYear, useHydrateYearFromStorage } from "@/state/yearStore";
import { compareCyclesNewestFirst } from "@/lib/bookingCycles";
import { CockpitTaskDefinitionFilter } from "@/components/tracker/CockpitTaskDefinitionFilter";
import { TrackerProgressExcelExportButton } from "@/components/tracker/TrackerProgressExcelExportButton";
import {
    copyTaskDefinitionIdsToParams,
    parseTaskDefinitionIds,
    serializeTaskDefinitionIds,
} from "@/lib/trackerTaskDefinitionFilter";

/* ─────────────────────────────────────────────────────────────────────────────
 * Cockpit page state persistence (localStorage)
 *
 * WHAT IS PERSISTED:
 *   year, retailerId, cycleId, primaryTab, and per-tab sub-state (e.g. review
 *   tab typeFilter / includeReviewed).
 *
 * WHY VALIDATION EXISTS:
 *   Stored values may become stale (retailer deleted, cycle archived, etc.).
 *   On restore we check every value against the currently-available option lists
 *   and silently discard anything invalid.
 *
 * HOW TO BUMP THE VERSION KEY:
 *   If the shape of PersistedCockpitState changes in a breaking way, increment
 *   the "_v1" suffix in STORAGE_KEY to "_v2" so old blobs are ignored.
 * ─────────────────────────────────────────────────────────────────────────── */

const STORAGE_KEY = "cockpit_review_approve_state_v1";

const KNOWN_PRIMARY_TABS = ["review", "operator", "campaign", "store", "settings", "by_operator"] as const;
type PrimaryTab = (typeof KNOWN_PRIMARY_TABS)[number];

export interface PersistedCockpitState {
    year?: number;
    retailerId?: string;
    cycleId?: string;
    taskDefinitionIds?: string;
    primaryTab?: PrimaryTab;
    subTabs?: {
        review?: { typeFilter?: string; includeReviewed?: boolean };
        operator?: Record<string, unknown>;
        campaign?: Record<string, unknown>;
        settings?: Record<string, unknown>;
    };
}

/** Read persisted state from localStorage, or null on any failure. */
export function readPersistedState(): PersistedCockpitState | null {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (typeof parsed !== "object" || parsed === null) return null;
        return parsed as PersistedCockpitState;
    } catch {
        return null;
    }
}

/** Merge partial update into persisted state and write to localStorage. */
export function writePersistedState(partial: Partial<PersistedCockpitState>): void {
    try {
        const existing = readPersistedState() || {};
        // Deep-merge subTabs
        const merged: PersistedCockpitState = {
            ...existing,
            ...partial,
            subTabs: {
                ...existing.subTabs,
                ...partial.subTabs,
            },
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
    } catch {
        // localStorage unavailable — silently ignore
    }
}

const TAB_PATH_MAP: Record<PrimaryTab, string> = {
    by_operator: "/admin/activation-cockpit/by-operator",
    review: "/admin/activation-cockpit/review",
    operator: "/admin/activation-cockpit/progress/operators",
    campaign: "/admin/activation-cockpit/progress/campaigns",
    store: "/admin/activation-cockpit/progress/stores",
    settings: "/admin/activation-cockpit/settings/rejection-reasons",
};

function derivePrimaryTab(pathname: string): PrimaryTab {
    if (pathname.includes("/by-operator")) return "by_operator";
    if (pathname.includes("/settings/rejection-reasons")) return "settings";
    if (pathname.includes("/recovery/") || pathname.includes("/substitute-posting/")) return "store";
    if (pathname.includes("/operators") || pathname.includes("/operator/")) return "operator";
    if (pathname.includes("/campaigns") || pathname.includes("/campaign/")) return "campaign";
    if (pathname.includes("/stores")) return "store";
    if (pathname.includes("/review")) return "review";
    // Default to review for unknown sub-paths within the cockpit
    return "review";
}

interface BookingCycle {
    id: number;
    name: string;
    startDate: string;
    endDate: string;
    status: string;
}

interface TrackerLayoutProps {
    children: React.ReactNode;
    title?: string;
    lastUpdated?: string | null;
    onRefresh?: () => void;
    isRefreshing?: boolean;
}

const REFRESH_LABEL_INTERVAL_MS = 30000;

const RelativeRefreshLabel = React.memo(function RelativeRefreshLabel({
    lastUpdated,
}: {
    lastUpdated: string;
}) {
    const [now, setNow] = React.useState(() => Date.now());

    useEffect(() => {
        const interval = window.setInterval(() => {
            setNow(Date.now());
        }, REFRESH_LABEL_INTERVAL_MS);

        return () => window.clearInterval(interval);
    }, []);

    const refreshDate = React.useMemo(() => new Date(lastUpdated), [lastUpdated]);
    const label = React.useMemo(() => {
        if (Number.isNaN(refreshDate.getTime())) {
            return "unknown";
        }
        return formatDistanceToNow(refreshDate, { addSuffix: true });
    }, [refreshDate, now]);

    if (Number.isNaN(refreshDate.getTime())) {
        return null;
    }

    return (
        <span title={format(refreshDate, "PPpp")}>
            {" · Last refreshed: "}
            {label}
        </span>
    );
});

export function TrackerLayout({
    children,
    title = "Tracker",
    lastUpdated,
    onRefresh,
    isRefreshing = false
}: TrackerLayoutProps) {
    const [location, setLocation] = useLocation();
    const searchString = useSearch();

    // Global year filter - hydrate from localStorage first
    useHydrateYearFromStorage();
    const year = useYear();
    const setYear = useSetYear();

    // Guard: prevents restore from re-triggering auto-select effects
    const isRestoringRef = useRef(false);
    const hasRestoredRef = useRef(false);

    // Get cycleId from query params
    const searchParams = new URLSearchParams(searchString);
    const currentCycleId = searchParams.get("cycleId");
    const currentRetailerId = searchParams.get("retailerId");
    const currentTaskDefinitionIds = searchParams.get("taskDefinitionIds");

    // Fetch retailers
    const { data: retailers, isLoading: retailersLoading } = useQuery<any[]>({
        queryKey: ['retailers'],
        queryFn: async () => {
            return await getJson<any[]>('/api/retailers');
        },
        staleTime: 30 * 60 * 1000 // 30 minutes
    });

    // Fetch cycles (dependent on retailer and year)
    const { data: cycles, isLoading: cyclesLoading } = useQuery<BookingCycle[]>({
        queryKey: ["booking-cycles", currentRetailerId, year],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (currentRetailerId) params.set("retailerId", currentRetailerId);
            params.set("year", String(year));
            return await getJson<BookingCycle[]>(`/api/booking-cycles?${params.toString()}`);
        },
        select: (data) => [...data].sort(compareCyclesNewestFirst),
        enabled: !!currentRetailerId || !retailersLoading,
        staleTime: 5 * 60 * 1000
    });

    // ── Restore persisted state on mount (once retailers+cycles are available) ──
    useEffect(() => {
        if (hasRestoredRef.current) return;
        // An explicitly scoped link wins over remembered filters, including on
        // remount when navigating from By Store to signature status.
        if (currentCycleId) {
            hasRestoredRef.current = true;
            return;
        }
        if (retailersLoading || !retailers || retailers.length === 0) return;
        // We need cycles to be loaded before we can validate cycleId.
        // But cycles depend on retailerId — so if no retailerId yet we first
        // need to apply the persisted retailerId to trigger a cycles fetch.
        const stored = readPersistedState();
        if (!stored) {
            hasRestoredRef.current = true;
            return;
        }

        // Validate & apply year
        if (stored.year && typeof stored.year === "number" && stored.year >= 2000 && stored.year <= 2100) {
            if (stored.year !== year) {
                setYear(stored.year);
            }
        }

        // Validate retailerId
        const validRetailer = stored.retailerId && retailers.some(r => r.id.toString() === stored.retailerId);
        const targetRetailerId = validRetailer ? stored.retailerId! : null;

        // If we don't have a retailerId in the URL yet, apply the persisted one
        if (!currentRetailerId && targetRetailerId) {
            isRestoringRef.current = true;
            const newParams = new URLSearchParams(window.location.search);
            newParams.set("retailerId", targetRetailerId);
            // Don't set cycle yet — wait for cycles to load for this retailer
            setLocation(`${window.location.pathname}?${newParams.toString()}`);
            // Don't mark as restored yet — we still need to apply cycle+tab once cycles load
            return;
        }

        // If we already have a retailerId and cycles are loaded, apply cycle+tab
        if (currentRetailerId && !cyclesLoading && cycles) {
            isRestoringRef.current = true;
            const newParams = new URLSearchParams(window.location.search);
            newParams.set("retailerId", currentRetailerId);

            // Validate cycleId
            const validCycle = stored.cycleId && cycles.some(c => c.id.toString() === stored.cycleId);
            if (validCycle) {
                newParams.set("cycleId", stored.cycleId!);
            } else {
                // Fall back to active or first cycle
                const fallback = cycles.find(c => c.status === "active") || cycles[0];
                if (fallback) newParams.set("cycleId", fallback.id.toString());
            }

            if (stored.taskDefinitionIds !== undefined) {
                if (stored.taskDefinitionIds === '') {
                    newParams.set("taskDefinitionIds", '');
                } else {
                    const parsed = parseTaskDefinitionIds(stored.taskDefinitionIds);
                    if (parsed === null) {
                        // Invalid persisted value — omit param (all tasks)
                    } else if (parsed.length === 0) {
                        newParams.set("taskDefinitionIds", '');
                    } else {
                        newParams.set("taskDefinitionIds", serializeTaskDefinitionIds(parsed));
                    }
                }
            }

            // Validate & apply primaryTab
            // Only redirect to the persisted tab if the user isn't already on a
            // specific cockpit tab — this prevents the restore from overriding
            // intentional tab navigation (since TrackerLayout remounts per-tab).
            const KNOWN_COCKPIT_PREFIXES = [
                ...Object.values(TAB_PATH_MAP),
                "/admin/activation-cockpit/operator/",
                "/admin/activation-cockpit/campaign/",
                "/admin/activation-cockpit/progress/stores",
                "/admin/activation-cockpit/recovery/",
                "/admin/activation-cockpit/substitute-posting/",
            ];
            const currentPathIsKnownTab = KNOWN_COCKPIT_PREFIXES.some(
                p => window.location.pathname.startsWith(p)
            );
            const validTab = stored.primaryTab && (KNOWN_PRIMARY_TABS as readonly string[]).includes(stored.primaryTab);
            const targetPath = (!currentPathIsKnownTab && validTab) ? TAB_PATH_MAP[stored.primaryTab!] : window.location.pathname;

            setLocation(`${targetPath}?${newParams.toString()}`);
            hasRestoredRef.current = true;
            // Allow the restoring flag to clear after this render cycle
            requestAnimationFrame(() => { isRestoringRef.current = false; });
            return;
        }

        // Edge case: no persisted retailer and no cycles yet — just mark done
        if (!targetRetailerId) {
            hasRestoredRef.current = true;
        }
    }, [retailers, retailersLoading, cycles, cyclesLoading, currentCycleId, currentRetailerId, year, setYear, setLocation]);

    // Auto-select first active cycle if none selected
    useEffect(() => {
        // Skip auto-select while restoring persisted state
        if (isRestoringRef.current) return;

        // Never reinterpret a record-specific recovery URL as another cycle,
        // even if its cycle is absent from the current year/filter options.
        // The scoped page/server must report unavailable or invalid context.
        if (location.includes('/recovery/') && currentCycleId) return;

        // First ensuring we have a retailer (default to first if none)
        if (retailersLoading || !retailers || retailers.length === 0) return;

        if (!currentRetailerId) {
            const firstRetailer = retailers[0];
            if (firstRetailer) {
                const newParams = new URLSearchParams(window.location.search);
                newParams.set("retailerId", firstRetailer.id.toString());
                // Don't auto-set cycle yet, let next effect pass do it
                setLocation(`${window.location.pathname}?${newParams.toString()}`);
            }
            return;
        }

        if (cyclesLoading || !cycles || cycles.length === 0) return;

        // If current cycle matches this retailer, we are good
        const cycleIsValid = currentCycleId && cycles.some(c => c.id.toString() === currentCycleId);

        if (!currentCycleId || !cycleIsValid) {
            const activeCycle = cycles.find(c => c.status === "active") || cycles[0];
            if (activeCycle) {
                const newParams = new URLSearchParams(window.location.search);
                if (currentRetailerId) newParams.set("retailerId", currentRetailerId);
                newParams.set("cycleId", activeCycle.id.toString());
                setLocation(`${window.location.pathname}?${newParams.toString()}`);
            }
        }
    }, [cycles, cyclesLoading, currentCycleId, currentRetailerId, retailers, retailersLoading, setLocation, searchString, location]);

    const handleRetailerChange = (value: string) => {
        const newParams = new URLSearchParams(window.location.search);
        newParams.set("retailerId", value);
        // Clear cycle and task filter when switching retailers
        newParams.delete("cycleId");
        newParams.delete("taskDefinitionIds");
        setLocation(`${window.location.pathname}?${newParams.toString()}`);
        writePersistedState({ retailerId: value, cycleId: undefined, taskDefinitionIds: undefined });
    }

    const handleCycleChange = (value: string) => {
        const newParams = new URLSearchParams(window.location.search);
        // Ensure retailerId is preserved
        if (currentRetailerId) newParams.set("retailerId", currentRetailerId);
        newParams.set("cycleId", value);
        newParams.delete("taskDefinitionIds");
        setLocation(`${window.location.pathname}?${newParams.toString()}`);
        writePersistedState({ cycleId: value, taskDefinitionIds: undefined });
    };

    const handleTaskDefinitionSearchChange = (nextSearch: string) => {
        const newParams = new URLSearchParams(nextSearch);
        if (currentRetailerId) newParams.set("retailerId", currentRetailerId);
        if (currentCycleId) newParams.set("cycleId", currentCycleId);
        const serialized = newParams.get("taskDefinitionIds");
        writePersistedState({ taskDefinitionIds: serialized ?? undefined });
        setLocation(`${window.location.pathname}?${newParams.toString()}`);
    };

    const selectedCycle = cycles?.find(c => c.id.toString() === currentCycleId);

    // Construct query string for tabs
    const cycleQueryObj = new URLSearchParams();
    if (currentRetailerId) cycleQueryObj.set("retailerId", currentRetailerId);
    if (currentCycleId) cycleQueryObj.set("cycleId", currentCycleId);
    copyTaskDefinitionIdsToParams(cycleQueryObj, searchString);
    const cycleQuery = cycleQueryObj.toString() ? `?${cycleQueryObj.toString()}` : "";

    // Determine active tab
    const isByOperator = location.includes("/by-operator");
    const isDashboard = location.includes("/dashboard");
    const isOperators = location.includes("/operators") || location.includes("/operator/");
    const isCampaigns = location.includes("/campaigns") || location.includes("/campaign/");
    const isStores = location.includes("/stores") || location.includes("/recovery/") || location.includes("/substitute-posting/");
    const isReview = location.includes("/review");
    const isSettings = location.includes("/settings/rejection-reasons");

    // ── Persist primary tab + year on route/year change ──
    useEffect(() => {
        if (isRestoringRef.current) return;
        const tab = derivePrimaryTab(location);
        writePersistedState({ primaryTab: tab, year });
    }, [location, year]);

    return (
        <div className="flex flex-col h-screen bg-gray-50/50">
            {/* Header section - styled like Campaigns page */}
            <div className="container mx-auto p-6">
                <PageHeader
                    icon={LayoutDashboard}
                    title={title}
                    description={
                        <span>
                            {selectedCycle?.name || "Select a cycle"}
                            {lastUpdated ? <RelativeRefreshLabel lastUpdated={lastUpdated} /> : null}
                        </span>
                    }
                >
                    {/* Year Filter */}
                    <YearFilter showIcon={false} />

                    {/* Retailer Selector */}
                    <div className="min-w-[200px]">
                        <p className="text-xs text-muted-foreground mb-1">Retailer</p>
                        <Select
                            value={currentRetailerId || undefined}
                            onValueChange={handleRetailerChange}
                            disabled={retailersLoading}
                        >
                            <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select Retailer" />
                            </SelectTrigger>
                            <SelectContent>
                                {retailers?.map(retailer => (
                                    <SelectItem key={retailer.id} value={retailer.id.toString()}>
                                        {retailer.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Cycle Selector */}
                    <div className="min-w-[180px]">
                        <p className="text-xs text-muted-foreground mb-1">Cycle</p>
                        <Select
                            value={currentCycleId || undefined}
                            onValueChange={handleCycleChange}
                            disabled={cyclesLoading || !currentRetailerId}
                        >
                            <SelectTrigger className="h-9">
                                <SelectValue placeholder={currentRetailerId ? "Select Cycle" : "Select Retailer First"} />
                            </SelectTrigger>
                            <SelectContent>
                                {cycles?.map(cycle => (
                                    <SelectItem key={cycle.id} value={cycle.id.toString()}>
                                        <div className="flex items-center justify-between w-full gap-2">
                                            <span>{cycle.name}</span>
                                            {cycle.status === "active" && (
                                                <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full uppercase font-bold tracking-wider">
                                                    Active
                                                </span>
                                            )}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <CockpitTaskDefinitionFilter
                        cycleId={currentCycleId}
                        searchString={searchString}
                        onSelectionChange={handleTaskDefinitionSearchChange}
                    />

                    {/* Export + Refresh */}
                    <div className="flex items-end gap-2 pb-0.5">
                        <TrackerProgressExcelExportButton
                            cycleId={currentCycleId}
                            retailerId={currentRetailerId}
                            taskDefinitionIds={currentTaskDefinitionIds}
                        />
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onRefresh}
                            disabled={isRefreshing}
                            className={cn("h-9", isRefreshing && "opacity-70")}
                        >
                            <RefreshCw className={cn("h-4 w-4 mr-2", isRefreshing && "animate-spin")} />
                            Refresh
                        </Button>
                    </div>
                </PageHeader>

                {/* Navigation Tabs */}
                <div className="flex items-center rounded-lg border bg-muted p-1 w-fit mb-6">
                    <Link
                        href={`/admin/activation-cockpit/progress/operators${cycleQuery}`}
                        className={cn(
                            "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                            isOperators
                                ? "bg-white text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        Progress
                    </Link>
                    <Link
                        href={`/admin/activation-cockpit/progress/stores${cycleQuery}`}
                        className={cn(
                            "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                            isStores
                                ? "bg-white text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        By Store
                    </Link>
                    <Link
                        href={`/admin/activation-cockpit/progress/campaigns${cycleQuery}`}
                        className={cn(
                            "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                            isCampaigns
                                ? "bg-white text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        By Campaign
                    </Link>
                    <Link
                        href={`/admin/activation-cockpit/review${cycleQuery}`}
                        className={cn(
                            "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                            isReview
                                ? "bg-white text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        By Task
                    </Link>
                    <Link href={`/admin/activation-cockpit/by-operator${cycleQuery}`} className={cn("px-3 py-1.5 text-sm font-medium rounded-md transition-colors", isByOperator ? "bg-white text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}>By Operator</Link>
                    <Link
                        href={`/admin/activation-cockpit/settings/rejection-reasons${cycleQuery}`}
                        className={cn(
                            "px-3 py-1.5 text-sm font-medium rounded-md transition-colors",
                            isSettings
                                ? "bg-white text-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground"
                        )}
                    >
                        Settings
                    </Link>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 overflow-auto p-6">{children}</div>
        </div>
    );
}

export default TrackerLayout;
